import { List, Map } from 'immutable'
import _map from 'lodash/map'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _isNil from 'lodash/isNil'
import _isArray from 'lodash/isArray'
import _isEmpty from 'lodash/isEmpty'
import _compact from 'lodash/compact'
import _filter from 'lodash/filter'
import _orderBy from 'lodash/orderBy'
import _reduce from 'lodash/reduce'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _toString from 'lodash/toString'
import {
  PLAN_SUBSCRIPTION_ANNUAL,
  PLAN_SUBSCRIPTION_MONTHLY,
  PLAN_SUBSCRIPTION_LIVE,
  PLAN_SUBSCRIPTION_UNKNOWN,
} from '.'

export const BILLING_PLAN_GRID_LOCATION_CHECKOUT = 'checkout'
export const BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN = 'planChange'
export const BILLING_PLAN_GRID_LOCATION_GIFT = 'gift'
export const BILLING_PLAN_GRID_LOCATION_CANCEL = 'cancel'

export const PLAN_GRID_DESKTOP_MIN_WIDTH = 1194

/**
 * Map viewport width to the billing plan grid device type.
 *
 * @param {number} [width] - Viewport width in pixels
 * @returns {'Mobile'|'Desktop'} `'Mobile'` when `0 < width < PLAN_GRID_DESKTOP_MIN_WIDTH`,
 *   else `'Desktop'`
 */
export function getPlanGridDeviceType (width) {
  return width > 0 && width < PLAN_GRID_DESKTOP_MIN_WIDTH ? 'Mobile' : 'Desktop'
}

export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_RECURRING = 'Recurring'
export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_GIFT = 'Gift'
export const BILLING_PLAN_PROVIDER_PRODUCT_TYPE_COURSE = 'Course'

export const BILLING_PLAN_TYPE_MONTHLY = 'Monthly'
export const BILLING_PLAN_TYPE_ANNUAL = 'Annual'
export const BILLING_PLAN_TYPE_GAIA_PLUS = 'Gaia+'

export const BILLING_PLAN_PERIOD_WEEK = 'week'
export const BILLING_PLAN_PERIOD_MONTH = 'month'
export const BILLING_PLAN_PERIOD_YEAR = 'year'

export const PRICE_REPLACEMENT_TYPE_NONE = 'none'
export const PRICE_REPLACEMENT_TYPE_PLAN = 'plan'
export const PRICE_REPLACEMENT_TYPE_NEXT_PLAN = 'nextPlan'
export const PRICE_REPLACEMENT_TYPE_FUTURE_PLAN = 'futurePlan'

/**
 * Free-trial users with a discount code and $0 first charge should not replace
 * the next-plan price via GraphQL (keep displayed trial/discount pricing).
 */
export function getPlanPriceReplacementType ({ inTrialSegment, nextPlan, replacementType }) {
  const discountCode = Map.isMap(nextPlan) ? nextPlan.get('discountCode') : undefined
  const charges = Map.isMap(nextPlan) ? nextPlan.get('charges') : undefined
  const firstCharge = List.isList(charges) ? charges.get(0) : undefined
  const firstChargePrice = Map.isMap(firstCharge) ? firstCharge.get('price') : undefined

  if (inTrialSegment && discountCode && firstChargePrice === '0.00') {
    return PRICE_REPLACEMENT_TYPE_NONE
  }

  return replacementType
}

/**
 * Charge price already on a billing plan when a discount code is applied.
 * Does not check GraphQL eligibility — the subscription already has the discount.
 * @param {import('immutable').Map|null|undefined} plan
 * @param {{ requireZeroCharge?: boolean }} [options]
 * @returns {string|number|null}
 */
export function getPlanDiscountChargePrice (plan, options = {}) {
  if (!Map.isMap(plan)) {
    return null
  }

  const discountCode = plan.get('discountCode')
  if (!discountCode) {
    return null
  }

  const charges = plan.get('charges')
  const firstCharge = List.isList(charges) ? charges.get(0) : undefined
  const price = Map.isMap(firstCharge) ? firstCharge.get('price') : undefined
  if (price === undefined || price === null || price === '') {
    return null
  }

  if (options.requireZeroCharge && price !== '0.00') {
    return null
  }

  return price
}

/**
 * Effective start date of the next plan's first charge when the user is in a
 * free-trial segment with a discount code and a $0 first charge.
 * @param {{ inTrialSegment: boolean, nextPlan: import('immutable').Map|null|undefined }} args
 * @returns {string|null}
 */
export function getTrialZeroDiscountNextPlanEffectiveStartDate ({ inTrialSegment, nextPlan }) {
  if (!inTrialSegment) {
    return null
  }

  if (getPlanDiscountChargePrice(nextPlan, { requireZeroCharge: true }) === null) {
    return null
  }

  const charges = Map.isMap(nextPlan) ? nextPlan.get('charges') : undefined
  const firstCharge = List.isList(charges) ? charges.get(0) : undefined
  const effectiveStartDate = Map.isMap(firstCharge) ? firstCharge.get('effectiveStartDate') : undefined

  return effectiveStartDate || null
}

/** Order summary row segments for {@link getCheckoutDiscountPriceDisplay} */
export const CHECKOUT_DISCOUNT_SEGMENT_DUE_NOW = 'dueNow'
export const CHECKOUT_DISCOUNT_SEGMENT_DUE_ON = 'dueOn'

export const PLAN_SCHEMA_LEGACY = {
  segments: [],
  heading: '',
  entitlementDays: null,
  shortDetails: '',
  costs: [],
  currencySymbol: '',
  billingPeriodAbbreviation: '',
  name: '',
  billingSchedule: '',
  duration: '',
  currencyIso: '',
  firstPayment: null,
  title: '',
  id: '',
  checkoutEnabled: true,
  billingPeriod: [],
  sku: '',
  valueProps: [],
  valuePropItems: [],
  legacyType: '',
  pricePerWeek: null,
}

/**
 * Normalize GET_USER_PLAN_GRID_LIST (or similar) payload for {@link formatBillingPlans}.
 *
 * GraphQL nests the grid under `userBillingPlanGridList.userBillingPlanGrids[]`. Select the row
 * whose `deviceType` matches; if missing, Desktop; if that is missing, the first row.
 *
 * Products may appear as `billingPlanGrid.billingProviderProducts` (user grid) or under
 * `billingPlanGrid.billingProviderProductList.billingProviderProducts` (same shape as the root
 * `GET_PLANS` query). {@link formatBillingPlans} only understands the latter, so we always emit
 * `plansData.billingProviderProductList.billingProviderProducts`.
 *
 * `bestValueBillingProviderProductId` is stringified (Lodash `toString`) so it lines up with
 * `billingProviderProduct.id` in {@link formatBillingPlans} when the API mixes Int and ID scalars.
 *
 * @param {Object} gqlPlanData - Apollo `data` for UserBillingPlanGridList
 * @param {string} [deviceType] - `'Mobile'` or `'Desktop'` row to select
 * @returns {{ plansData: Object, bestValueBillingProviderProductId: string|null }}
 */
export function userPlanGridPayloadForFormat (gqlPlanData, deviceType) {
  if (!gqlPlanData) {
    return { plansData: {}, bestValueBillingProviderProductId: null }
  }

  const grids = _get(gqlPlanData, [
    'userBillingPlanGridList',
    'userBillingPlanGrids',
  ]) || []
  const matchingRow = _find(grids, { deviceType })
    || _find(grids, { deviceType: 'Desktop' })
    || grids[0]
  const billingPlanGrid = _get(matchingRow, 'billingPlanGrid')

  if (!billingPlanGrid) {
    return { plansData: {}, bestValueBillingProviderProductId: null }
  }

  const nestedList = _get(billingPlanGrid, 'billingProviderProductList')
  const nestedProducts = _get(nestedList, 'billingProviderProducts')
  const directProducts = _get(billingPlanGrid, 'billingProviderProducts')
  let billingProviderProducts = []

  // Prefer GET_PLANS-shaped nesting when present; otherwise use the list on BillingPlanGrid.
  // _compact removes nulls from GraphQL Maybe[] product lists (both branches).
  if (_isArray(nestedProducts) && !_isEmpty(nestedProducts)) {
    billingProviderProducts = _compact(nestedProducts)
  } else if (_isArray(directProducts)) {
    billingProviderProducts = _compact(directProducts)
  }

  const rawBestValueId = _get(billingPlanGrid, 'bestValueBillingProviderProductId')
  const bestValueBillingProviderProductId = _isNil(rawBestValueId) || rawBestValueId === ''
    ? null
    : _toString(rawBestValueId)

  return {
    plansData: {
      billingProviderProductList: { billingProviderProducts },
    },
    bestValueBillingProviderProductId,
  }
}

/**
 * Format/transform GraphQL plan data into the legacy plan data shape
 * @param {Object} plansData - GraphQL payload containing billingProviderProductList
 * @param {string|null} bestValueId - Billing provider product id for best value
 *   (must match GraphQL billingProviderProduct id; sets legacy bestValue)
 * @returns {Array} - Formatted array of plan data objects in source order
 */
export function formatBillingPlans (plansData = {}, bestValueId = null) {
  if (!plansData) {
    return []
  }

  const plans = _get(plansData, ['billingProviderProductList', 'billingProviderProducts']) || []

  return _map(plans, (plan) => {
    const costs = getBillingPlanCosts(plan) || []

    return (_assign(_cloneDeep(PLAN_SCHEMA_LEGACY), {
      id: _get(plan, 'providerFirstSegmentId'),
      sku: _get(plan, 'sku', ''),
      type: _get(plan, ['billingProduct', 'name']),
      entitlementContentSet: _get(plan, ['billingProduct', 'contentSetId']),
      legacyType: getBillingPlanLegacyType(plan),
      subscriptionType: _get(plan, 'type'),
      compositeType: `${_get(plan, 'termLength')}${_get(plan, 'termType')}${_get(plan, ['billingProduct', 'name'])}${costs[0] || '0'}`,
      title: getBillingPlanTextItemValue(plan, 'title'),
      heading: getBillingPlanTextItemValue(plan, 'heading'),
      name: getBillingPlanTextItemValue(plan, 'name'),
      duration: getBillingPlanTextItemValue(plan, 'duration'),
      firstPayment: _get(costs, 0),
      billingPeriodAbbreviation: getBillingPlanTextItemValue(plan, 'billingPeriodAbbreviation'),
      billingSchedule: getBillingPlanTextItemValue(plan, 'billingSchedule'),
      billingPeriod: [
        getBillingPlanTextItemValue(plan, 'billingPeriod'),
      ],
      shortDetails: getBillingPlanTextItemValue(plan, 'shortDetails'),
      promoValue1: getBillingPlanTextItemValue(plan, 'promoValue1'),
      promoValue2: getBillingPlanTextItemValue(plan, 'promoValue2'),
      valuePropItems: getBillingPlanTextValuePropItems(plan),
      valueProps: getBillingPlanTextValueProps(plan),
      currencyIso: getBillingPlanPriceItemValue(plan, 'currency'),
      currencySymbol: getBillingPlanPriceItemValue(plan, 'currencySymbol'),
      checkoutEnabled: _get(plan, 'checkoutEnabled'),
      providerFirstSegmentId: _get(plan, 'providerFirstSegmentId'),
      providerSecondSegmentId: _get(plan, 'providerSecondSegmentId'),
      percentSavings: getBillingPlanPriceItemValue(plan, 'percentSavings'),
      trialPercentSavings: getBillingPlanPriceItemValue(plan, 'trialPercentSavings'),
      pricePerWeek: getBillingPlanPriceItemValue(plan, 'pricePerWeek'),
      costs,
      marketingValue: getBillingPlanPriceItemValue(plan, 'marketingValue'),
      segments: getBillingPlanSegments(plan),
      changePlanAllowed: _get(plan, 'allowChangePlan'),
      bestValue: _get(plan, 'id') === bestValueId,
    }))
  })
}

/**
 * Get plan text item value
 * @param {Object} plan - The billing plan
 * @param {String} key - They object key of the desired text item
 * @returns {String} - The plan text item value
 */
function getBillingPlanTextItemValue (plan, key) {
  if (!plan || !key) {
    return null
  }
  const textItem = _find(_get(plan, 'billingProviderProductText', {}), (i) => _get(i, 'key') === key && _get(i, 'enabled')) || {}
  return _get(textItem, 'value') || ''
}

/**
 * Get plan pricing item
 * @param {Object} plan - The billing plan
 * @param {String} key - They object key of the desired pricing item
 * @returns {String} - The plan pricing item
 */
function getBillingPlanPriceItemValue (plan, key) {
  if (!plan || !key) {
    return null
  }
  const priceItem = _get(plan, ['billingProviderProductPrices', 0], {}) || {}
  return _get(priceItem, key)
}

/**
 * Enabled billingProviderProductText rows that are value props, ordered by valuePropOrder.
 * @param {Object} plan - The billing plan
 * @returns {Array<Object>}
 */
function getOrderedEnabledValueProps (plan) {
  const valueProps = _filter(_get(plan, 'billingProviderProductText'), (i) => _get(i, 'isValueProp') && _get(i, 'enabled')) || []
  return _orderBy(valueProps, ['valuePropOrder'], ['asc'])
}

/**
 * Get enabled value prop items with display text and optional ecommerceSku.
 * Ordered by valuePropOrder ascending (same as getBillingPlanTextValueProps).
 * @param {Object} plan - The billing plan
 * @returns {Array<{ value: string, ecommerceSku: string|null }>}
 */
export function getBillingPlanTextValuePropItems (plan) {
  return _map(getOrderedEnabledValueProps(plan), (valueProp) => ({
    value: _get(valueProp, 'value') || '',
    ecommerceSku: _get(valueProp, 'ecommerceSku') || null,
  }))
}

/**
 * Get the plan enabled value props
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan value props that are enabled
 */
function getBillingPlanTextValueProps (plan) {
  const orderedValueProps = getOrderedEnabledValueProps(plan)
  return _reduce(orderedValueProps, (acc, valueProp) => {
    acc.push(_get(valueProp, 'value'))
    return acc
  }, [])
}

/**
 * Whether this plan is the user's already-selected membership on account change plan.
 * Matches PlanCard "current plan" CTA / styling behavior.
 * @param {Object} options
 * @param {boolean} [options.isAccountChangePlan=true]
 * @param {boolean} [options.userPlanIsExactMatch=false] - compositeType matches current plan
 * @param {boolean} [options.userNextPlanIsExactMatch=false] - compositeType matches next plan
 * @param {boolean} [options.cancellationScheduled=false]
 * @returns {boolean}
 */
export function isPlanAlreadySelected ({
  isAccountChangePlan = true,
  userPlanIsExactMatch = false,
  userNextPlanIsExactMatch = false,
  cancellationScheduled = false,
} = {}) {
  return Boolean(
    isAccountChangePlan
    && (userPlanIsExactMatch || userNextPlanIsExactMatch)
    && !cancellationScheduled,
  )
}

/**
 * Filter account-change-plan value props for display on PlanCard.
 * Props without an ecommerceSku are always included.
 * Props with an ecommerceSku:
 * - on other plans (not already selected): always shown
 * - on the user's already-selected plan: only shown when they have a matching
 *   ecommerce_sku entitlement
 * @param {List|Array<{ value: string, ecommerceSku?: string|null }>} valuePropItems
 * @param {Set<string>} userEcommerceSkus - entitlement ecommerce_sku values
 * @param {Object} [options]
 * @param {boolean} [options.planAlreadySelected=false] - same as PlanCard
 *   planAlreadySelected (current/next plan match, not cancelling)
 * @returns {string[]} display strings for PlanCard valueProps
 */
export function filterAccountChangePlanValueProps (
  valuePropItems,
  userEcommerceSkus,
  { planAlreadySelected = false } = {},
) {
  const items = List.isList(valuePropItems)
    ? valuePropItems
    : List(_isArray(valuePropItems) ? valuePropItems : [])

  return items.reduce((acc, item) => {
    const value = Map.isMap(item) ? item.get('value') : _get(item, 'value')
    const ecommerceSku = Map.isMap(item) ? item.get('ecommerceSku') : _get(item, 'ecommerceSku')
    if (!ecommerceSku || !planAlreadySelected || userEcommerceSkus.has(ecommerceSku)) {
      acc.push(value)
    }
    return acc
  }, [])
}

/**
 * Get plan billing segments
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan billing segments
 */
function getBillingPlanSegments (plan) {
  const {
    termLength,
    termType,
    trialLength,
    trialTermType,
  } = plan
  const currency = getBillingPlanPriceItemValue(plan, 'currency')
  const trialPrice = getBillingPlanPriceItemValue(plan, 'trialSegmentPrice')
  const price = getBillingPlanPriceItemValue(plan, 'price')
  const segments = []

  if (trialLength) {
    segments.push({
      period: getFormattedBillingPlanPeriod(trialTermType, trialLength),
      price: _toString(trialPrice),
      currencyIso: currency,
    })
  }

  segments.push({
    period: getFormattedBillingPlanPeriod(termType, termLength),
    price: _toString(price),
    currencyIso: currency,
  })

  return segments
}

/**
 * Convert the plan type to the legacy plan type
 * @param {Object} plan - The billing plan
 * @returns {String} - The plan legacy type
 */
export function getBillingPlanLegacyType (plan) {
  const realType = _get(plan, ['billingProduct', 'name'])

  switch (realType) {
    case BILLING_PLAN_TYPE_MONTHLY:
      return PLAN_SUBSCRIPTION_MONTHLY
    case BILLING_PLAN_TYPE_ANNUAL:
      return PLAN_SUBSCRIPTION_ANNUAL
    case BILLING_PLAN_TYPE_GAIA_PLUS:
      return PLAN_SUBSCRIPTION_LIVE
    default:
      return PLAN_SUBSCRIPTION_UNKNOWN
  }
}

/**
 * Get the formatted billing period
 * @param {Object} plan - The billing plan
 * @returns {String} - The plan legacy billing period
 */
function getFormattedBillingPlanPeriod (termType, termLength) {
  const periodAbbr = 'P'
  let length = termLength
  let term = 'M'

  // transform "1 year" to "12 months"
  if (termType === BILLING_PLAN_PERIOD_YEAR) {
    length = 12
  }

  // transform "1 week" to "7 days"
  if (termType === BILLING_PLAN_PERIOD_WEEK) {
    term = 'D'
    length = 7 * termLength
  }

  return `${periodAbbr}${length}${term}`
}

/**
 * Get the plan costs
 * @param {Object} plan - The billing plan
 * @returns {Array} - The plan costs
 */
function getBillingPlanCosts (plan) {
  const trialPrice = getBillingPlanPriceItemValue(plan, 'trialSegmentPrice')
  const price = getBillingPlanPriceItemValue(plan, 'price')
  const costs = []

  // allow "zero" to be added as a cost
  // if trialPrice is null or undefined, then there is no trial
  if (!_isNil(trialPrice)) {
    costs.push(_toString(trialPrice))
  }

  costs.push(_toString(price))

  return costs
}

/**
 * Extract discount eligibility from a PlansBySku GraphQL response
 * @param {Object} planData - GraphQL response data
 * @param {String} sku - Plan SKU to match
 * @returns {Object|null} - Raw discount object or null
 */
export function extractDiscountEligibility (planData, sku) {
  if (!planData || !sku) {
    return null
  }
  const products = _get(planData, ['billingProviderProductList', 'billingProviderProducts'], [])
  const product = _find(products, (p) => _get(p, 'sku') === sku)
  return _get(product, 'discount') || null
}

/**
 * Build GET_PLANS_BY_SKU variables with optional discount code
 * @param {Object} options
 * @param {String} options.language - Provider product language
 * @param {String|String[]} options.skus - Plan SKU or SKUs
 * @param {String} [options.discountCode] - Discount code when checking eligibility
 * @param {String} [options.priceReplacementType] - Subscription pricing replacement type
 * @returns {Object} - GraphQL query variables
 */
export function getPlansBySkuDiscountVariables ({
  language,
  skus,
  discountCode,
  priceReplacementType = PRICE_REPLACEMENT_TYPE_NONE,
}) {
  const includeDiscount = Boolean(discountCode)
  return {
    language,
    skus,
    priceReplacementType,
    includeDiscount,
    discountCode: discountCode || '',
  }
}

function getEligibleDiscountPricing (discountEligibility) {
  if (!Map.isMap(discountEligibility) || discountEligibility.get('eligible') !== true) {
    return null
  }

  const discountedPricing = discountEligibility.get('discountedPricing')
  if (!Map.isMap(discountedPricing) || !discountedPricing.size) {
    return null
  }

  return discountedPricing
}

function buildCheckoutDiscountPriceDisplay ({
  originalAmount,
  discountedAmount,
  discountedFormatted,
  currencyIso,
  showCurrencyIso,
  userLanguage,
  formatPriceFn,
  preTax,
}) {
  if (_isNil(originalAmount) || _isNil(discountedAmount)) {
    return null
  }

  if (_toString(originalAmount) === _toString(discountedAmount)) {
    return null
  }

  const preTaxSuffix = preTax || ''
  const originalFormatted = formatPriceFn(
    currencyIso,
    originalAmount,
    false,
    null,
    userLanguage,
  )
  const discountedBase = discountedFormatted
    || formatPriceFn(
      currencyIso,
      discountedAmount,
      false,
      null,
      userLanguage,
    )
  const nextDiscountedFormatted = discountedFormatted
    ? `${discountedBase}${preTaxSuffix}`
    : `${discountedBase}${showCurrencyIso ? ` ${currencyIso}` : ''}${preTaxSuffix}`

  return {
    originalFormatted,
    discountedFormatted: nextDiscountedFormatted,
  }
}

/**
 * Build strikethrough + discounted price display for checkout order summary rows.
 * @param {Object} options
 * @param {import('immutable').Map} options.discountEligibility - Checkout discount eligibility
 * @param {import('immutable').Map} options.selectedPlan - Selected checkout plan
 * @param {'dueNow'|'dueOn'} options.segment - Which order summary row to price
 *   (`CHECKOUT_DISCOUNT_SEGMENT_DUE_NOW` or `CHECKOUT_DISCOUNT_SEGMENT_DUE_ON`)
 * @param {Function} options.formatPriceFn - formatPrice from plans service
 * @param {boolean} options.showCurrencyIso - Whether to append currency ISO
 * @param {string} options.userLanguage - User language for formatting
 * @param {string|null} [options.preTax] - Optional pre-tax suffix
 * @returns {{ originalFormatted: string, discountedFormatted: string }|null}
 */
export function getCheckoutDiscountPriceDisplay ({
  discountEligibility,
  selectedPlan,
  segment,
  formatPriceFn,
  showCurrencyIso,
  userLanguage,
  preTax = null,
}) {
  const discountedPricing = getEligibleDiscountPricing(discountEligibility)
  if (!discountedPricing || !Map.isMap(selectedPlan) || !selectedPlan.size) {
    return null
  }

  const costs = selectedPlan.get('costs', List())
  if (!List.isList(costs) || !costs.size) {
    return null
  }

  const currencyIso = selectedPlan.get('currencyIso') || discountedPricing.get('currency')
  const hasTrialCost = costs.size > 1

  if (segment === CHECKOUT_DISCOUNT_SEGMENT_DUE_NOW) {
    if (hasTrialCost && discountedPricing.get('trialSegmentPriceDiscounted') === true) {
      return buildCheckoutDiscountPriceDisplay({
        originalAmount: costs.first(),
        discountedAmount: discountedPricing.get('trialSegmentPrice'),
        discountedFormatted: discountedPricing.get('trialSegmentPriceFormatted'),
        currencyIso,
        showCurrencyIso,
        userLanguage,
        formatPriceFn,
        preTax,
      })
    }

    if (!hasTrialCost && discountedPricing.get('recurringPrice1Discounted') === true) {
      return buildCheckoutDiscountPriceDisplay({
        originalAmount: costs.last(),
        discountedAmount: discountedPricing.get('recurringPrice1'),
        discountedFormatted: discountedPricing.get('recurringPrice1Formatted'),
        currencyIso,
        showCurrencyIso,
        userLanguage,
        formatPriceFn,
        preTax,
      })
    }

    return null
  }

  if (segment === CHECKOUT_DISCOUNT_SEGMENT_DUE_ON && discountedPricing.get('recurringPrice1Discounted') === true) {
    return buildCheckoutDiscountPriceDisplay({
      originalAmount: costs.last(),
      discountedAmount: discountedPricing.get('recurringPrice1'),
      discountedFormatted: discountedPricing.get('recurringPrice1Formatted'),
      currencyIso,
      showCurrencyIso,
      userLanguage,
      formatPriceFn,
      preTax,
    })
  }

  return null
}

/**
 * Plan cost passed to Adyen Drop-in (due-now amount), with discount applied when eligible.
 * @param {import('immutable').Map} selectedPlan
 * @param {import('immutable').Map} discountEligibility
 * @returns {number|string}
 */
export function getCheckoutPaymentPlanCost (selectedPlan, discountEligibility) {
  if (!Map.isMap(selectedPlan) || !selectedPlan.size) {
    return 0
  }

  const costs = selectedPlan.get('costs', List())
  if (!List.isList(costs) || !costs.size) {
    return 0
  }

  const discountedPricing = getEligibleDiscountPricing(discountEligibility)
  const hasTrialCost = costs.size > 1

  if (discountedPricing) {
    if (hasTrialCost && discountedPricing.get('trialSegmentPriceDiscounted') === true) {
      return discountedPricing.get('trialSegmentPrice')
    }

    if (!hasTrialCost && discountedPricing.get('recurringPrice1Discounted') === true) {
      return discountedPricing.get('recurringPrice1')
    }
  }

  return costs.first()
}
